// [S-411 / E-MULTI-TENANT] User menu in the sidebar foot. Clicking the
// name/avatar opens a popover with: workspace switching (when the user belongs
// to >1 tenant) and Sign out. Replaces the standalone switcher dropdown.
//
// Split into a pure presentational `UserMenuView` (testable with props) and the
// context-wired `UserMenu`.
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import { useActiveTenant } from "../auth/activeTenantContext";
import type { WebMembership } from "../auth/activeTenantContext";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0]!.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function prettyTenant(tenantId: string): string {
  return tenantId
    .replace(/^tenant-/, "")
    .split("-")
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

export type UserMenuViewProps = {
  displayName: string;
  roleLabel: string;
  studioLabel: string;
  memberships: WebMembership[];
  activeTenantId: string | null;
  onSwitch: (tenantId: string) => void;
  onSignOut: () => void;
  /** Opens the support form ("Report a problem"). Omitted → no menu entry. */
  onReportProblem?: () => void;
  /** Opens the caller's own tickets. Omitted → no menu entry. */
  onMyTickets?: () => void;
};

export function UserMenuView({
  displayName,
  roleLabel,
  studioLabel,
  memberships,
  activeTenantId,
  onSwitch,
  onSignOut,
  onReportProblem,
  onMyTickets,
}: UserMenuViewProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const showWorkspaces = memberships.length > 1;

  return (
    <div className="sb__usermenu" ref={ref}>
      {open && (
        <div className="sb__menu" role="menu" aria-label="Account menu">
          {showWorkspaces && (
            <>
              <div className="sb__menu-label">Switch workspace</div>
              {memberships.map((m) => {
                const isActive = m.tenantId === activeTenantId;
                return (
                  <button
                    key={m.tenantId}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    className={
                      isActive ? "sb__menu-item is-active" : "sb__menu-item"
                    }
                    onClick={() => {
                      onSwitch(m.tenantId);
                      setOpen(false);
                    }}
                  >
                    <span className="sb__menu-check" aria-hidden="true">
                      {isActive ? "✓" : ""}
                    </span>
                    <span className="sb__menu-name">{prettyTenant(m.tenantId)}</span>
                    <span className="sb__menu-role">
                      {m.role === "tenant_admin" ? "Admin" : "Member"}
                    </span>
                  </button>
                );
              })}
              <div className="sb__menu-sep" role="separator" />
            </>
          )}
          {onReportProblem !== undefined && (
            <button
              type="button"
              role="menuitem"
              className="sb__menu-item"
              onClick={() => {
                onReportProblem();
                setOpen(false);
              }}
              data-testid="sb-report-problem"
            >
              <span className="sb__menu-check" aria-hidden="true" />
              <span className="sb__menu-name">Report a problem</span>
            </button>
          )}
          {onMyTickets !== undefined && (
            <button
              type="button"
              role="menuitem"
              className="sb__menu-item"
              onClick={() => {
                onMyTickets();
                setOpen(false);
              }}
              data-testid="sb-my-tickets"
            >
              <span className="sb__menu-check" aria-hidden="true" />
              <span className="sb__menu-name">My support tickets</span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="sb__menu-item sb__menu-item--signout"
            onClick={onSignOut}
            data-testid="sb-signout"
          >
            <span className="sb__menu-check" aria-hidden="true" />
            <span className="sb__menu-name">Sign out</span>
          </button>
        </div>
      )}

      <button
        type="button"
        className="sb__foot sb__foot--btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="sb-usermenu-trigger"
      >
        <span className="sb__avatar">{initials(displayName)}</span>
        <div className="sb__user">
          <div className="sb__user-name">{displayName}</div>
          <div className="sb__user-meta">
            {roleLabel} · {studioLabel}
          </div>
        </div>
        <Chevrons />
      </button>
    </div>
  );
}

function prettyRole(role: string | undefined): string {
  switch (role) {
    case "platform_admin":
      return "Platform Admin";
    case "tenant_admin":
      return "Organization admin";
    case "tenant_user":
      return "Attorney";
    default:
      return "—";
  }
}

export function UserMenu(): JSX.Element {
  const session = useSession();
  const { signOut } = useAuth();
  const active = useActiveTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const studioLabel =
    session === null
      ? "—"
      : session.studioId === null
        ? "ContractStudio"
        : prettyTenant(`tenant-${session.studioId}`);
  return (
    <UserMenuView
      displayName={session?.displayName ?? "Loading"}
      roleLabel={prettyRole(session?.role)}
      studioLabel={studioLabel}
      memberships={active?.memberships ?? []}
      activeTenantId={active?.activeTenantId ?? null}
      onSwitch={(t) => active?.switchTenant(t)}
      onSignOut={() => void signOut()}
      onReportProblem={() =>
        navigate(
          `/support?from=${encodeURIComponent(location.pathname + location.search)}`
        )
      }
      // Platform staff get every tenant's list from the sidebar instead.
      {...(sessionCan(session, "platform:manage_tenants", { layer: "L1" })
        ? {}
        : { onMyTickets: () => navigate("/support/tickets") })}
    />
  );
}

function Chevrons(): ReactNode {
  return (
    <svg
      className="sb__foot-caret"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 9l4-4 4 4M16 15l-4 4-4-4" />
    </svg>
  );
}
